import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, ArrowRight, Loader2, Check, Building2, MapPin, Image } from 'lucide-react';
import { toast } from 'sonner';
import { useBusinessAuth } from '@/context/BusinessAuthContext';

const API_URL = '';

const CATEGORIES = [
  { value: 'eat', label: 'Eat' },
  { value: 'do', label: 'Do' },
  { value: 'explore', label: 'Explore' },
  { value: 'experiences', label: 'Experiences' },
  { value: 'transport', label: 'Transport' },
  { value: 'wellness', label: 'Wellness' },
  { value: 'nightlife', label: 'Nightlife' },
  { value: 'shopping', label: 'Shopping' },
];

const STEPS = [
  { id: 'payment', title: 'Payment', icon: Check },
  { id: 'details', title: 'Business Details', icon: Building2 },
  { id: 'location', title: 'Location', icon: MapPin },
  { id: 'media', title: 'Media', icon: Image },
];

export default function OnboardingPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useBusinessAuth();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [tier, setTier] = useState('starter');
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'eat',
    phone: '',
    website: '',
    address: '',
    image_url: '',
    image_credit: ''
  });

  // Check payment status on mount
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const tierParam = searchParams.get('tier');
    
    if (tierParam) {
      setTier(tierParam);
    }
    
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setCheckingPayment(false);
      // No session_id means direct access - check if user has active subscription
      checkExistingSubscription();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const checkExistingSubscription = async () => {
    try {
      const response = await fetch(`${API_URL}/api/partner/portal/subscription`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.subscription?.status === 'active') {
          setPaymentStatus('paid');
          setTier(data.subscription.tier);
          setCurrentStep(1); // Skip to details step
        }
      }
    } catch (error) {
      console.error('Failed to check subscription:', error);
    } finally {
      setCheckingPayment(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      setPaymentStatus('timeout');
      toast.error('Payment verification timed out. Please contact support.');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/partner/checkout/status/${sessionId}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();
      
      if (data.payment_status === 'paid') {
        setPaymentStatus('paid');
        setCurrentStep(1);
        setCheckingPayment(false);
        await refreshUser();
        toast.success('Payment successful!');
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
        setCheckingPayment(false);
        toast.error('Payment session expired. Please try again.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Payment status check failed:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    // Validate current step
    if (currentStep === 1) {
      if (!formData.name || !formData.description || !formData.phone) {
        toast.error('Please fill in all required fields');
        return;
      }
    } else if (currentStep === 2) {
      if (!formData.address) {
        toast.error('Please enter your business address');
        return;
      }
    }
    
    setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1)); // Can't go back to payment step
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/partner/onboarding/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create business');
      }

      const data = await response.json();
      toast.success('Business created successfully!');
      await refreshUser();
      
      // Redirect to portal
      navigate('/portal');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (checkingPayment) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="onboarding-loading">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-teal-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  // Payment failed/expired
  if (paymentStatus === 'expired' || paymentStatus === 'timeout') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6" data-testid="onboarding-error">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-3xl">⚠️</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Payment Issue</h1>
          <p className="text-slate-600 mb-6">
            We couldn't verify your payment. Please try again or contact support.
          </p>
          <Button
            onClick={() => navigate('/partner')}
            className="h-12 px-6 rounded-full bg-teal-600 hover:bg-teal-700"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="onboarding-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 px-6 py-4">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-xl font-bold text-slate-900 font-heading">
            Set up your business listing
          </h1>
          <p className="text-sm text-slate-500">
            {tier.charAt(0).toUpperCase() + tier.slice(1)} Plan
          </p>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="bg-white border-b border-slate-100 px-6 py-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium
                  ${index < currentStep 
                    ? 'bg-teal-600 text-white' 
                    : index === currentStep 
                      ? 'bg-teal-100 text-teal-700 border-2 border-teal-600' 
                      : 'bg-slate-100 text-slate-400'
                  }
                `}>
                  {index < currentStep ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <step.icon className="w-5 h-5" />
                  )}
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`
                    w-12 sm:w-20 h-1 mx-2
                    ${index < currentStep ? 'bg-teal-600' : 'bg-slate-200'}
                  `} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <main className="px-6 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Step 0: Payment Confirmed */}
          {currentStep === 0 && (
            <div className="bg-white rounded-2xl p-8 text-center border border-slate-100 shadow-sm">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 font-heading mb-2">
                Payment Confirmed!
              </h2>
                <p className="text-sm text-slate-600 mb-6">
                Your {tier} plan is now active. Let&apos;s set up your business listing.
              </p>
              <Button
                onClick={() => setCurrentStep(1)}
                className="h-12 px-8 rounded-full bg-teal-600 hover:bg-teal-700"
                data-testid="continue-to-details"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* Step 1: Business Details */}
          {currentStep === 1 && (
            <div className="bg-white rounded-2xl p-6 sm:p-8 border border-slate-100 shadow-sm">
              <h2 className="text-xl font-bold text-slate-900 font-heading mb-6">
                Business Details
              </h2>
              
              <div className="space-y-5">
                <div>
                  <Label htmlFor="name">Business Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="Your business name"
                    className="h-12 mt-1.5"
                    data-testid="input-name"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="Describe your business in 2-3 sentences"
                    rows={3}
                    className="mt-1.5"
                    data-testid="input-description"
                  />
                </div>

                <div>
                  <Label>Category *</Label>
                  <Select value={formData.category} onValueChange={(v) => handleChange('category', v)}>
                    <SelectTrigger className="h-12 mt-1.5" data-testid="select-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="+61 7 1234 5678"
                    className="h-12 mt-1.5"
                    data-testid="input-phone"
                  />
                </div>

                {tier !== 'starter' && (
                  <div>
                    <Label htmlFor="website">Website URL</Label>
                    <Input
                      id="website"
                      value={formData.website}
                      onChange={(e) => handleChange('website', e.target.value)}
                      placeholder="https://yourbusiness.com"
                      className="h-12 mt-1.5"
                      data-testid="input-website"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Available with your {tier} plan
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Location */}
          {currentStep === 2 && (
            <div className="bg-white rounded-2xl p-6 sm:p-8 border border-slate-100 shadow-sm">
              <h2 className="text-xl font-bold text-slate-900 font-heading mb-6">
                Business Location
              </h2>
              
              <div className="space-y-5">
                <div>
                  <Label htmlFor="address">Full Address *</Label>
                  <Textarea
                    id="address"
                    value={formData.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    placeholder="123 Main Street, Surfers Paradise QLD 4217"
                    rows={2}
                    className="mt-1.5"
                    data-testid="input-address"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {tier !== 'starter' 
                      ? 'This address will be used for map directions'
                      : 'This will be displayed on your listing'
                    }
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Media */}
          {currentStep === 3 && (
            <div className="bg-white rounded-2xl p-6 sm:p-8 border border-slate-100 shadow-sm">
              <h2 className="text-xl font-bold text-slate-900 font-heading mb-6">
                Business Image
              </h2>
              
              <div className="space-y-5">
                <div>
                  <Label htmlFor="image_url">Image URL</Label>
                  <Input
                    id="image_url"
                    value={formData.image_url}
                    onChange={(e) => handleChange('image_url', e.target.value)}
                    placeholder="https://images.unsplash.com/..."
                    className="h-12 mt-1.5"
                    data-testid="input-image-url"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Paste a URL to an image that represents your business
                  </p>
                </div>

                {formData.image_url && (
                  <div>
                    <Label>Preview</Label>
                    <img
                      src={formData.image_url}
                      alt="Preview"
                      className="w-full h-48 object-cover rounded-xl mt-1.5"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}

                <div>
                  <Label htmlFor="image_credit">Image Credit (optional)</Label>
                  <Input
                    id="image_credit"
                    value={formData.image_credit}
                    onChange={(e) => handleChange('image_credit', e.target.value)}
                    placeholder="Unsplash / Your Name"
                    className="h-12 mt-1.5"
                    data-testid="input-image-credit"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          {currentStep > 0 && (
            <div className="flex gap-4 mt-6">
              {currentStep > 1 && (
                <Button
                  variant="outline"
                  onClick={handleBack}
                  className="flex-1 h-12 rounded-full"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              )}
              
              {currentStep < STEPS.length - 1 ? (
                <Button
                  onClick={handleNext}
                  className="flex-1 h-12 rounded-full bg-teal-600 hover:bg-teal-700"
                  data-testid="next-step-btn"
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 h-12 rounded-full bg-teal-600 hover:bg-teal-700"
                  data-testid="submit-listing-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Create Listing
                      <Check className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
